import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { getAllYoutubeData } from '../services/youtubeService';
import { getUploadDefaults, UploadDefault } from '../services/uploadDefaultsService';
import { useAuth } from '../components/auth/AuthContext';
import { getUser } from '../utils/localStorageManager';
import { usePermissions } from '../hooks/usePermissions';

interface AppDataContextValue {
  youtubeData: {
    categories: { id: string; name: string }[] | null;
    playlists: { id: string; name: string }[];
    playlistsNextPageToken: string | null;
  } | null;
  uploadDefaults: UploadDefault[] | null; // Full upload default data
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  fetchYouTubeData: (force?: boolean) => Promise<void>;
  fetchUploadDefaults: () => Promise<void>;
  // Deprecated: ensureYouTubeData, ensureUploadDefaults
  ensureYouTubeData?: () => Promise<void>;
  ensureUploadDefaults?: () => Promise<void>;
}

const AppDataContext = createContext<AppDataContextValue | undefined>(undefined);

interface AppDataProviderProps {
  children: ReactNode;
}

export function AppDataProvider({ children }: AppDataProviderProps) {
  const { isAuthenticated } = useAuth();
  const { hasFeature, isLoading: permissionsLoading } = usePermissions();
  const [youtubeData, setYoutubeData] = useState<{
    categories: { id: string; name: string }[] | null;
    playlists: { id: string; name: string }[];
    playlistsNextPageToken: string | null;
  } | null>(null);
  const [uploadDefaults, setUploadDefaults] = useState<UploadDefault[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const youtubeFetchingRef = useRef<boolean>(false);
  const uploadDefaultsFetchingRef = useRef<boolean>(false);
  const uploadDefaults403Ref = useRef<boolean>(false); // Track if we got a 403 to prevent retries
  const youtube401Ref = useRef<boolean>(false); // Track if we got a 401 to prevent retries
  const youtube404Ref = useRef<boolean>(false); // Track if we got a 404 (no channel) to prevent retries

  const fetchYouTubeData = useCallback(async (force: boolean = false) => {
    if (youtubeFetchingRef.current) {
      return; // Already fetching
    }

    // Don't retry if we got a 401 or 404 (prevents retry loop)
    // Unless force is true (e.g., after OAuth callback)
    if ((youtube401Ref.current || youtube404Ref.current) && !force) {
      return;
    }

    // Check if user changed - clear data if so
    const user = getUser();
    const userId = user?.id || null;
    if (userId && userId !== currentUserId) {
      setCurrentUserId(userId);
      setYoutubeData(null);
      setUploadDefaults(null);
      youtube401Ref.current = false; // Reset 401 flag on user change
      youtube404Ref.current = false; // Reset 404 flag on user change
    }

    try {
      youtubeFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      youtube401Ref.current = false; // Reset 401 flag on new attempt
      youtube404Ref.current = false; // Reset 404 flag on new attempt
      // Reset youtubeData to null if forcing (after OAuth)
      if (force) {
        setYoutubeData(null);
      }

      const result = await getAllYoutubeData();
        setYoutubeData({
        categories: result.categories,
        playlists: result.playlists,
        playlistsNextPageToken: result.playlistsNextPageToken,
      });
      youtube401Ref.current = false; // Clear 401 flag on success
      youtube404Ref.current = false; // Clear 404 flag on success
    } catch (err: any) {
      const is401 = err?.response?.status === 401;
      const is404 = err?.response?.status === 404;

      if (is401) {
        // Mark that we got a 401 to prevent retry loops
        youtube401Ref.current = true;
        // Set to empty object (not null) to prevent retries
        setYoutubeData({ categories: [], playlists: [], playlistsNextPageToken: null });
        // Let apiClient interceptor handle redirect - don't set error
      } else if (is404) {
        // User doesn't have a YouTube channel - this is expected for some users
        youtube404Ref.current = true;
        // Set to empty arrays since they have no channel
        setYoutubeData({ categories: [], playlists: [], playlistsNextPageToken: null });
        setError(err?.response?.data?.message || 'Please create a YouTube channel to use this app.');
      } else {
        console.error('Error fetching YouTube data:', err);
        setError(err?.response?.data?.message || err?.message || 'Failed to load YouTube data');
      }
    } finally {
      setIsLoading(false);
      youtubeFetchingRef.current = false;
      }
  }, [currentUserId]);

  const fetchUploadDefaults = useCallback(async () => {
    if (uploadDefaultsFetchingRef.current) {
      return; // Already fetching
    }

    try {
      uploadDefaultsFetchingRef.current = true;
      setIsLoading(true);
      setError(null);
      uploadDefaults403Ref.current = false; // Reset 403 flag on new attempt
      
      const result = await getUploadDefaults();
      // Store full data, filtering out any without id
      setUploadDefaults(result.filter(d => d.id !== undefined) as UploadDefault[]);
    } catch (err: any) {
      const is403 = err?.response?.status === 403;
      if (is403) {
        // Mark that we got a 403 to prevent retry loops
        uploadDefaults403Ref.current = true;
        // Set to empty array (not null) to prevent useEffect from retrying
        setUploadDefaults([]);
      } else {
        console.error('Error fetching upload defaults:', err);
        setError(err?.response?.data?.message || err?.message || 'Failed to load upload defaults');
        setUploadDefaults(null);
      }
    } finally {
      setIsLoading(false);
      uploadDefaultsFetchingRef.current = false;
    }
  }, []);

  const ensureYouTubeData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    const user = getUser();
    const userId = user?.id || null;
    
    // Check if user changed - clear data if so
    if (userId && userId !== currentUserId) {
      setCurrentUserId(userId);
      setYoutubeData(null);
      setUploadDefaults(null);
      youtube401Ref.current = false; // Reset 401 flag on user change
    }
    
    // Fetch if we don't have data
    if (youtubeData === null && !youtubeFetchingRef.current) {
      await fetchYouTubeData();
    }
  }, [isAuthenticated, currentUserId, youtubeData, fetchYouTubeData]);

  const ensureUploadDefaults = useCallback(async () => {
    if (!isAuthenticated) return;
    
    // Wait for permissions to load before checking
    if (permissionsLoading) return;
    
    // Check permission before fetching - prevent 403 errors
    if (!hasFeature('upload_defaults')) {
      // User doesn't have permission, set to empty array to prevent retries
      setUploadDefaults([]);
      uploadDefaults403Ref.current = true;
      return;
    }

    const user = getUser();
    const userId = user?.id || null;
    
    // Check if user changed - clear data if so
    if (userId && userId !== currentUserId) {
      setCurrentUserId(userId);
      setYoutubeData(null);
      setUploadDefaults(null);
      uploadDefaults403Ref.current = false; // Reset 403 flag on user change
      youtube401Ref.current = false; // Reset 401 flag on user change
    }
    
    // Don't fetch if we already got a 403 (prevents retry loop)
    if (uploadDefaults403Ref.current) {
      return;
    }
    
    // Fetch if we don't have data
    if (uploadDefaults === null && !uploadDefaultsFetchingRef.current) {
      await fetchUploadDefaults();
    }
  }, [isAuthenticated, permissionsLoading, hasFeature, currentUserId, uploadDefaults, fetchUploadDefaults]);

  const refresh = useCallback(async () => {
    setYoutubeData(null);
    setUploadDefaults(null);
    youtubeFetchingRef.current = false;
    uploadDefaultsFetchingRef.current = false;
    youtube401Ref.current = false; // Reset 401 flag on refresh
    uploadDefaults403Ref.current = false; // Reset 403 flag on refresh
    await Promise.all([fetchYouTubeData(), fetchUploadDefaults()]);
  }, [fetchYouTubeData, fetchUploadDefaults]);

  // Reset 403 flag when user gains permission (e.g., upgrades)
  useEffect(() => {
    if (!permissionsLoading && hasFeature('upload_defaults') && uploadDefaults403Ref.current) {
      uploadDefaults403Ref.current = false;
      // Reset to null so it can be fetched again
      if (uploadDefaults && uploadDefaults.length === 0) {
        setUploadDefaults(null);
      }
    }
  }, [permissionsLoading, hasFeature, uploadDefaults]);

  // Clear data on logout
  useEffect(() => {
    if (!isAuthenticated) {
      setYoutubeData(null);
      setUploadDefaults(null);
      setCurrentUserId(null);
      setIsLoading(false);
      setError(null);
      youtubeFetchingRef.current = false;
      uploadDefaultsFetchingRef.current = false;
      uploadDefaults403Ref.current = false;
      youtube401Ref.current = false; // Reset 401 flag on logout
    }
  }, [isAuthenticated]);

  const value: AppDataContextValue = {
    youtubeData,
    uploadDefaults,
    isLoading,
    error,
    refresh,
    fetchYouTubeData,
    fetchUploadDefaults,
    // Keep ensure functions for backward compatibility (deprecated)
    ensureYouTubeData,
    ensureUploadDefaults,
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
}

export function useYouTubeData() {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error('useYouTubeData must be used within an AppDataProvider');
  }
  
  // NO useEffect auto-fetch - components call fetchYouTubeData explicitly

  return {
    youtubeData: context.youtubeData,
    isLoading: context.isLoading,
    error: context.error,
    fetchYouTubeData: context.fetchYouTubeData,
    refresh: context.refresh,
  };
}

export function useUploadDefaults() {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error('useUploadDefaults must be used within an AppDataProvider');
  }
  
  // NO useEffect auto-fetch - components call fetchUploadDefaults explicitly

  return {
    uploadDefaults: context.uploadDefaults,
    isLoading: context.isLoading,
    error: context.error,
    fetchUploadDefaults: context.fetchUploadDefaults,
    refresh: context.refresh,
  };
}

